import { type FC, useMemo, useCallback } from 'react';
import { Formik, Form, type FormikHelpers } from 'formik';
import LayoutFooter from '@/registration/component/Register/Layout/Footer';
import BaseTransitionSlideLeft from '@/base/Transition/SlideLeft';
import {
  UiButton,
  UiHStack, UiIconCaretLeft, UiIconCaretRight,
  UiStack,
  UiText,
} from '@/lib/ui';
import FormGenerator from '@/base/FormGenerator';
import { createUtils, type FieldsValues } from '@/base/FormGenerator/utils';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  saveInfoFormResponse,
  type InfoFormResponseSaveRequest,
  FormType,
} from '@/api/registration/info_form_response';
import QueryContainer from '@/base/QueryContainer/QueryContainer';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { type InfoFormByAttendeeCategoryQuery, type InfoForm, InfoFormType, type InfoFormResponse, loadRegistrationInfoFormResponse, infoFormResponseQueryKey, infoFormByAttendeeCategoryQueryKey, loadInfoFormByAttendeeCategory } from '@/api/registration';
import { Skeleton } from '@chakra-ui/react';
import { useRegisterSteps } from '@/registration/hook/useRegisterSteps';
import { useRegistrationQuery } from '@/registration/hook/useRegistrationQuery';
import { useEventSettingsQuery } from '@/registration/hook/useEventSettingsQuery';
import { type FieldMetadataInput } from '@/base/FormBuilder/Field';
import InfoBox from '@/registration/component/InfoBox';
import { validatePhoneNumber } from '@/lib/util/helper';
import { type CountryCode } from 'libphonenumber-js';
import BrandFooter from '@/registration/component/Register/Layout/BrandFooter';
import BaseThemeButton from '@/base/ThemeButton';

type FormData = FieldsValues;
type FieldName = keyof FieldsValues;

type Errors = Record<FieldName, string>;

export interface AdditionalInfoFormProps { }

const AdditionalInfoForm: FC<AdditionalInfoFormProps> = () => {
  const { eventId } = useRegisterRoute();
  const queryClient = useQueryClient();
  const registrationQuery = useRegistrationQuery(eventId);
  const { data: eventSetting } = useEventSettingsQuery(eventId);
  const { toNextStep, toPreviousStep, isReviewing } = useRegisterSteps();
  const { createTenantApiRequest, isLoading: isApiPreparing } = useTenantApi();

  const additionalInfoQuery = useQuery<InfoFormResponse, Error>({
    queryKey: [infoFormResponseQueryKey, { registrationId: registrationQuery?.data?.id, type: InfoFormType.ADDITIONAL }],
    queryFn: async () => {
      if (!registrationQuery?.data?.id) {
        throw new Error('Invalid registrationId');
      }
      const response = await loadRegistrationInfoFormResponse(createTenantApiRequest)({
        registrationId: registrationQuery.data.id,
        type: InfoFormType.ADDITIONAL
      });
      return response?.item ?? {};
    },
    enabled: !!registrationQuery?.data?.id && !isApiPreparing,
  });

  const infoFormByAttendeeCategoryQuery = useQuery<InfoForm, Error>(
    [infoFormByAttendeeCategoryQueryKey, { registrationId: registrationQuery?.data?.id, formType: InfoFormType.ADDITIONAL }],
    async () => {
      if (typeof registrationQuery?.data?.id === 'undefined') {
        return await Promise.reject(new Error('Invalid registrationId'));
      }
      const params: InfoFormByAttendeeCategoryQuery = { formType: InfoFormType.ADDITIONAL, registrationId: registrationQuery?.data?.id };
      const result = await loadInfoFormByAttendeeCategory(createTenantApiRequest)(params);
      if (result.item === undefined) {
        throw new Error(result.errors?.join('\n') ?? 'Something went wrong');
      }
      return result.item;
    },
    {
      enabled: !!registrationQuery?.data?.id && !isApiPreparing,
      cacheTime: 0 // Disable caching
    }
  );

  const utils = useMemo(
    () => { return createUtils((infoFormByAttendeeCategoryQuery.data?.config?.fieldsMetadata ?? [])); },
    [infoFormByAttendeeCategoryQuery.data?.config?.fieldsMetadata]);

  const { mutate, isLoading } = useMutation<{}, Error, InfoFormResponseSaveRequest>({
    mutationFn: async (data: InfoFormResponseSaveRequest) => {
      return await saveInfoFormResponse(createTenantApiRequest)(data);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [infoFormByAttendeeCategoryQueryKey] });
      await queryClient.invalidateQueries([infoFormResponseQueryKey, { registrationId: registrationQuery?.data?.id, type: InfoFormType.ADDITIONAL }]);
      toNextStep();
    }
    // onError: (error) => {}
  });

  const submitForm = useCallback(async (values: FormData) => {
    if (!registrationQuery?.data?.id) {
      return;
    }

    await mutate({
      eventId,
      formType: 'additional' as FormType,
      response: values,
      ownerId: registrationQuery?.data?.id ?? 0,
      ownerType: 'Registration',
      infoFormId: infoFormByAttendeeCategoryQuery?.data?.id ?? 0,
    });
  }, [
    eventId,
    infoFormByAttendeeCategoryQuery,
    mutate,
    registrationQuery?.data?.id,
  ]);

  if (infoFormByAttendeeCategoryQuery.isLoading) {
    return <Skeleton height='80px' />;
  }

  return (
    <QueryContainer query={infoFormByAttendeeCategoryQuery}>
      {(infoForm: InfoForm) => {
        return (
          <QueryContainer query={additionalInfoQuery}>
            {(additionInfo) => {
              return (
                <Formik
                  initialValues={utils.getFieldsValues(additionInfo.response)}
                  validateOnChange={false}
                  validateOnBlur={false}
                  validate={(values: FormData) => {
                    const errors: Errors = {};
                    // This is an example to check 'required' fields. We might validator others in the future.
                    for (const fieldId in values) {
                      const fieldMetadata = utils.getFieldMetadata(fieldId);
                      if (fieldMetadata) {
                        // check for phone type
                        if ('isRequired' in fieldMetadata && fieldMetadata.isRequired && fieldMetadata.type === 'phone') {
                          const phoneValue = (values[fieldId] as string ?? '').replaceAll(' ', '');
                          const currentDialCode = values[`${fieldId}-countryCode`] as string;
                          const isValid = validatePhoneNumber({ phone: phoneValue, country: currentDialCode as CountryCode });
                          if (!isValid) {
                            errors[fieldId] = 'Invalid phone number';
                          }
                        }

                        // If a field contains exmpty string, we will treat it as empty. Currently we don't consider number or boolean.
                        if ('isRequired' in fieldMetadata && fieldMetadata.isRequired && values[fieldId] === '') {
                          errors[fieldId] = 'The field is required';
                        }
                        if ((fieldMetadata as FieldMetadataInput).length && values[fieldId]) {
                          if ((values[fieldId] as string)?.length > (fieldMetadata as FieldMetadataInput).length!) {
                            errors[fieldId] = `The field can not have more than ${(fieldMetadata as FieldMetadataInput).length} characters.`;
                          }
                        }
                      }
                    }
                    return errors;
                  }}
                  onSubmit={async (
                    values,
                    // { setSubmitting }: FormikHelpers<FormData>
                    { setSubmitting }: FormikHelpers<any>
                  ) => {
                    const formKeys = Object.keys(utils.getFieldsValues(additionInfo.response));
                    const formValues = formKeys.reduce((acc: Record<string, any>, key) => {
                      acc[key] = values[key];
                      return acc;
                    }, {});
                    setSubmitting(true);
                    await submitForm(formValues);
                    setSubmitting(false);
                  }}
                >
                  <Form>
                    <BaseTransitionSlideLeft>
                      <UiStack flexGrow={1}>
                        {/* {eventSetting?.pageMetadata?.additionalInfo?.title && (
                          <UiText fontWeight={600} variant="h6">
                            {eventSetting?.pageMetadata?.additionalInfo?.title}
                          </UiText>
                        )}
                        {eventSetting?.pageMetadata?.additionalInfo?.description && (
                          <UiText variant={'body1'} color={'text.secondary'} pb={4}>
                            {eventSetting?.pageMetadata?.additionalInfo?.description}
                          </UiText>
                        )} */}
                        {(eventSetting?.pageMetadata?.additionalInfo?.title || eventSetting?.pageMetadata?.additionalInfo?.description) && (
                          <InfoBox
                            title={eventSetting?.pageMetadata?.additionalInfo?.title}
                            description={eventSetting?.pageMetadata?.additionalInfo?.description}
                          />
                        )}
                        <FormGenerator
                          fieldsLayout={infoForm.config.fieldsLayout}
                          fieldsMetadata={infoForm.config.fieldsMetadata}
                        />
                      </UiStack>
                    </BaseTransitionSlideLeft>
                    <UiStack height={96} />
                    <LayoutFooter>
                      <UiHStack justifyContent={'space-between'} flexGrow={1}>
                        {/*<UiButton*/}
                        {/*  size={'lg'}*/}
                        {/*  colorScheme={'primary'}*/}
                        {/*  onClick={toPreviousStep}*/}
                        {/*  variant={'ghost'}*/}
                        {/*  fontSize={'lg'}*/}
                        {/*  leftIcon={(*/}
                        {/*    <UiIconCaretLeft color={'primary.800'} size={'xl'} weight={'bold'}/>*/}
                        {/*  )}*/}
                        {/*>*/}
                        {/*  {isReviewing ? 'Back' : 'Prev'}*/}
                        {/*</UiButton>*/}
                        {/*<BrandFooter/>*/}
                        {/*<UiButton*/}
                        {/*  size={'lg'}*/}
                        {/*  colorScheme={'primary'}*/}
                        {/*  type={'submit'}*/}
                        {/*  isLoading={isLoading}*/}
                        {/*  variant={'ghost'}*/}
                        {/*  fontSize={'lg'}*/}
                        {/*  rightIcon={(*/}
                        {/*    <UiIconCaretRight color={'primary.800'} size={'xl'} weight={'bold'}/>*/}
                        {/*  )}*/}
                        {/*>*/}
                        {/*  {isReviewing ? 'Done' : 'Next'}*/}
                        {/*</UiButton>*/}

                        <BaseThemeButton
                          theme={'gray'}
                          onClick={toPreviousStep}
                          leftIcon={(
                            <UiIconCaretLeft color={'primary.800'} size={'lg'} weight={'bold'}/>
                          )}
                        >
                          {isReviewing ? 'Back' : 'Prev'}
                        </BaseThemeButton>
                        <BrandFooter/>
                        <BaseThemeButton
                          theme={'primary'}
                          type={'submit'}
                          isLoading={isLoading}
                          rightIcon={(
                            <UiIconCaretRight color={'primary.800'} size={'lg'} weight={'bold'}/>
                          )}
                        >
                          {isReviewing ? 'Done' : 'Next'}
                        </BaseThemeButton>
                      </UiHStack>
                    </LayoutFooter>
                  </Form>
                </Formik>
              );
            }}
          </QueryContainer>
        );
      }}
    </QueryContainer>
  );
};

export default React.memo(AdditionalInfoForm);
